import * as React from 'react';
import cn from 'classnames';
import { mergeClasses } from '@tcl-argon-utils/utils';
import { ButtonProps } from '@tcl-boron-prefabs/button';
import { ActionPalette, BrandPalette, GrayscalePalette } from '@tcl-boron-colors/colors';
import Loader from '@tcl-boron-prefabs/loader';
import { useState } from 'react';
import { uniqueId } from 'lodash-es';
import styles from './iconButton.module.css';

export type Icon = React.ComponentType<{ className?: string; height?: string | number; width?: string | number }>;

export interface IconButtonProps extends Omit<ButtonProps, 'buttonType' | 'minWidth' | 'Loader' | 'allCaps'> {
  /** An icon component to be displayed in place of a label. Pass in the component directly i.e. `<IconButton Icon={Success} name="Click here to be successful" />`. This can technically be anything, but should be a TCL Icon. */
  Icon: React.ComponentType<{ className?: string; height?: string | number; width?: string | number }>;
  /** Because IconButtons have no inner text, they MUST include a "name" to be considered accessible. The name should describe what happens when the button is clicked. */
  name: string;
  /** If true, disable all user interaction for the IconButton. */
  disabled?: boolean;
  small?: boolean;
  /** label passed to the button for screen reader use */
  ariaLabel: string;
  /** when true, icon button is set to active state */
  isActive?: boolean;
}

export const IconButton: React.FC<IconButtonProps> = ({
  Icon,
  classes: classOverrides,
  className,
  name = 'Icon Button',
  disabled = false,
  small = false,
  loading = false,
  ariaLabel,
  isActive = false,
  loaderProps = { isComplete: false, Icon: undefined },
  id,
  ...props
}) => {
  [id] = useState(id || uniqueId('icon_button_'));
  const [isHovered, setIsHovered] = useState(false);
  const classes = mergeClasses(styles, classOverrides, {
    root: className,
  });
  const isDisabled = disabled || loading;

  return (
    <button
      aria-label={ariaLabel}
      className={cn(classes.root, className, { [classes.disabled]: disabled })}
      name={name}
      disabled={isDisabled}
      data-analytics="icon-button"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      key={id}
      style={
        {
          '--tcl-icon-button-width': small ? '32px' : '48px',
          '--tcl-icon-button-background-color': loading
            ? `${ActionPalette[200]}`
            : isActive
            ? `${ActionPalette[600]}`
            : 'transparent',
          '--tcl-icon-button-border-color': loading
            ? `${ActionPalette[200]}`
            : isActive
            ? `${ActionPalette[600]}`
            : 'transparent',
          '--tcl-icon-button-focus-border-color': ActionPalette[300],
          '--tcl-icon-button-focus-background-color': isActive ? ActionPalette[600] : 'transparent',
          '--tcl-icon-button-hover-border-color': ActionPalette[200],
          '--tcl-icon-button-hover-background-color': ActionPalette[200],
          '--tcl-icon-button-active-border-color': ActionPalette[400],
          '--tcl-icon-button-color': disabled
            ? GrayscalePalette[400]
            : isHovered
            ? BrandPalette[700]
            : isActive
            ? ActionPalette[100]
            : BrandPalette[700],
        } as React.CSSProperties
      }
      {...props}
    >
      {loading && (
        <div className={classes.loader}>
          {
            <Loader
              size={loaderProps.Icon !== undefined ? 'large' : small ? 'small' : 'large'}
              isComplete={loaderProps.isComplete}
              Icon={loaderProps.Icon}
            />
          }
        </div>
      )}
      <div className={cn(classes.children, { [classes.loading]: loading })}>
        <Icon
          aria-hidden="true"
          height={small ? 14 : 16}
          width={small ? 14 : 16}
          className={cn(classes.icon, { [classes.iconDisabled]: isDisabled })}
          key={`icon_${id}`}
        />
      </div>
    </button>
  );
};

IconButton['isIconButton'] = true;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isIconButton(component: any): component is React.ReactElement<IconButtonProps> {
  return component.type?.isIconButton === true;
}
